/**
 * Blocco 5 · Badge di stato sincronizzazione Google Calendar per task/eventi.
 *
 * Stati visivi:
 *  - error   → ⚠ rosso · click apre popover con dettaglio + bottone "Riprova"
 *  - synced  → ☁ verde ("Sincronizzato")
 *  - pending → ⏳ grigio ("In coda")
 *  - none    → non renderizza nulla
 *
 * Props:
 *  - task: oggetto task con almeno { id, show_on_google_calendar, linked_calendar_id,
 *          google_event_id, google_sync_error, google_synced_at }
 *  - onRetry?: () => Promise — chiamato quando l'utente clicca "Riprova"
 *  - compact?: boolean — versione senza label (solo icona) per righe strette
 */
import { useState } from "react";
import { Cloud, Loader2, AlertTriangle, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function computeStatus(task) {
  if (!task) return "none";
  const enabled = !!(task.show_on_google_calendar && task.linked_calendar_id);
  if (!enabled) return "none";
  if (task.google_sync_error) return "error";
  if (task.google_event_id) return "synced";
  return "pending";
}

const CFG = {
  error:   { icon: AlertTriangle, bg: "#FDE7EB", fg: "#942038", border: "#F4A9B7", label: "Errore sync" },
  synced:  { icon: Cloud,         bg: "#E6F5EC", fg: "#1F6B3A", border: "#B7DEC3", label: "Sincronizzato" },
  pending: { icon: Loader2,       bg: "#F3F4F6", fg: "#4B5563", border: "#D1D5DB", label: "In coda" },
};

export default function GoogleSyncBadge({ task, onRetry, compact = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = computeStatus(task);
  if (status === "none") return null;
  const c = CFG[status];
  const Icon = c.icon;
  const clickable = status === "error";

  const doRetry = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      const r = onRetry
        ? await onRetry()
        : await api.googleRetrySync(task.id);
      if (r?.ok) {
        toast.success("Sincronizzazione riuscita");
        setOpen(false);
      } else {
        toast.error(`Sync ancora in errore: ${r?.error || "errore sconosciuto"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile ritentare la sync");
    } finally { setBusy(false); }
  };

  const badge = (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${clickable ? "cursor-pointer hover:opacity-80" : ""}`}
      style={{ background: c.bg, color: c.fg, borderColor: c.border }}
      data-testid={`gsync-badge-${status}`}
      onClick={clickable ? (e) => { e.stopPropagation(); setOpen(true); } : undefined}
      title={c.label + (status === "error" && task.google_sync_error ? ` — ${task.google_sync_error}` : "")}
    >
      <Icon size={10} className={status === "pending" ? "animate-spin" : ""} />
      {!compact && <span>{c.label}</span>}
    </span>
  );

  if (!open) return badge;

  return (
    <>
      {badge}
      <div
        className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
        onClick={() => setOpen(false)}
        data-testid="gsync-error-modal"
      >
        <div
          className="bg-white rounded-2xl max-w-md w-full p-5 space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-600"/>
              <h3 className="font-display text-lg font-semibold">Sync Google Calendar fallita</h3>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Chiudi"><X size={18}/></button>
          </div>
          <div className="text-sm opacity-90">
            <b>Task:</b> {task.title}
          </div>
          <div className="text-sm rounded-md p-3" style={{ background: "#FDE7EB", color: "#942038" }}>
            <b>Errore:</b> {task.google_sync_error || "Errore sconosciuto"}
          </div>
          <div className="text-xs opacity-70">
            Il task è comunque salvato nell'app. Il badge tornerà verde appena la sync riesce.
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={() => setOpen(false)} className="text-sm underline opacity-70">Chiudi</button>
            <button
              disabled={busy}
              onClick={doRetry}
              className="pxos-btn-primary text-sm inline-flex items-center gap-2"
              data-testid="gsync-retry-btn"
            >
              {busy ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
              Riprova ora
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
